/**
 * Klassenliste-specific endpoints for liveOverviewApi.
 * Class info, table preferences, custom columns, BuT.
 */
import { supabase } from '../../../../lib/supabase';
import { liveOverviewApi } from './liveOverviewApi';

// ── Bundle return type ──────────────────────────────────────────────────────────
export interface ListeBundleData {
    students: Array<{
        id: string; first_name: string; last_name: string;
        class_name: string | null; class_id: string | null;
        but_type: string | null; but_valid_until: string | null;
        deutsch_herkunftssprache: boolean | null;
        herkunftssprache_code: string | null;
        herkunftssprache_name: string | null;
        allergies: string | null; important_info: string | null; sensitive_data: string | null;
        date_of_birth: string | null; address: string | null;
        photo_consent: {
            id: string;
            print_publications: boolean;
            press: boolean;
            school_website: boolean;
            project_sites: boolean;
            publish_name_and_class: boolean;
            comment: string | null;
            valid_from: string;
            valid_until: string | null;
            granted_by: string | null;
        } | null;
        pickup: Record<string, string>;
        course_ids: string[];
    }>;
    custom_columns: Array<{
        id: string; label: string; type: string; visibility: string;
        options: string[] | null; class_id: string | null; ordering: number | null;
    }>;
    custom_column_values: Array<{
        student_id: string; column_id: string; value: string;
    }>;
    preferences: {
        id: string; column_order: string[]; column_visibility: Record<string, boolean>;
        column_widths: Record<string, number>; sort_field: string | null;
        sort_direction: string; group_by: string | null; filters: any[] | null;
        course_ids: string[] | null;
        view_name: string | null;
    } | null;
    views: Array<{
        id: string; view_name: string; visibility: 'private' | 'shared';
        user_profile_id: string; class_id: string | null;
        column_order: string[]; column_visibility: Record<string, boolean>;
        column_widths: Record<string, number>; sort_field: string | null;
        sort_direction: string; group_by: string | null; filters: any[] | null;
        course_ids: string[] | null;
        shared_with_users: string[] | null; shared_with_groups: string[] | null;
        updated_at: string;
    }>;
    courses: Array<{ ids: string[]; name: string; min_day_id: number | null; min_start_time: string | null; class_ids: string[]; teacher_ids: string[] }>;
    heritage_languages: Array<{ code: string; name: string }>;
    class_teachers: Record<string, { primary: string[]; additional: string[]; class_info?: string }>;
    class_info: string;
    class_info_print: boolean;
}

const klassenlisteApi = liveOverviewApi.injectEndpoints({
    endpoints: (builder) => ({
        // ── Single bundle RPC ─────────────────────────────────────────────
        getListeBundle: builder.query<ListeBundleData, { classId: string | null; semesterId: string | null; courseIds?: string[] | null }>({
            async queryFn({ classId, semesterId, courseIds }) {
                try {
                    const params: Record<string, unknown> = {};
                    if (classId) params.p_class_id = classId;
                    if (semesterId) params.p_semester_id = semesterId;
                    if (courseIds && courseIds.length > 0) params.p_course_ids = courseIds;
                    const { data, error } = await supabase.rpc(
                        'fn_klassenliste_bundle' as never,
                        params as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: data as ListeBundleData };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId, courseIds }) => [
                { type: 'ListeOverview', id: courseIds?.join(',') || classId || 'ALL' },
                { type: 'TablePrefs', id: courseIds?.join(',') || classId || 'global' },
                { type: 'TableViews', id: courseIds?.join(',') || classId || 'global' },
                { type: 'CustomColumns', id: courseIds?.join(',') || classId || 'all' },
            ],
            keepUnusedDataFor: 60,
        }),


        getClassInfo: builder.query<
            { class_info: string; class_info_print: boolean },
            { classId: string }
        >({
            async queryFn({ classId }) {
                try {
                    if (!classId) return { data: { class_info: '', class_info_print: true } };
                    // Lookup by id — keep structure_classes so archived/historical classes still resolve.
                    const { data, error } = await supabase
                        .from('structure_classes' as never)
                        .select('class_info, class_info_print')
                        .eq('id' as never, classId as never)
                        .maybeSingle();
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return {
                        data: {
                            class_info: (data as any)?.class_info || '',
                            class_info_print: (data as any)?.class_info_print ?? true,
                        }
                    };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId }) => [{ type: 'ClassInfo', id: classId }],
            keepUnusedDataFor: 120,
        }),

        updateClassInfo: builder.mutation<
            void,
            { classId: string; classInfo: string; classInfoPrint: boolean }
        >({
            async queryFn({ classId, classInfo, classInfoPrint }) {
                try {
                    const { error } = await supabase.rpc(
                        'fn_klassenliste_update_class_info' as never,
                        {
                            p_class_id: classId,
                            p_class_info: classInfo,
                            p_class_info_print: classInfoPrint,
                        } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            invalidatesTags: (_r, _e, { classId }) => [{ type: 'ClassInfo' as const, id: classId }],
        }),

        getTablePreferences: builder.query<
            { column_order: string[]; column_visibility: Record<string, boolean>; column_widths: Record<string, number>; sort_field: string | null; sort_direction: string; group_by: string | null; filters: any[] | null } | null,
            { classId: string | null }
        >({  
            async queryFn({ classId }) {
                try {
                    let query = supabase
                        .from('user_table_preferences' as never)
                        .select('column_order, column_visibility, column_widths, sort_field, sort_direction, group_by, filters')
                        .eq('table_key' as never, 'klassenliste' as never)
                        .eq('view_name' as never, 'Standard' as never);

                    if (classId) {
                        query = query.eq('class_id' as never, classId as never);
                    } else {
                        query = query.is('class_id' as never, null as never);
                    }

                    const { data, error } = await query.maybeSingle();

                    if (error) {
                        console.error('[getTablePrefs] query error:', error);
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }

                    if (!data) {
                        if (classId) {
                            const { data: globalData, error: globalError } = await supabase
                                .from('user_table_preferences' as never)
                                .select('column_order, column_visibility, column_widths, sort_field, sort_direction, group_by, filters')
                                .eq('table_key' as never, 'klassenliste' as never)
                                .eq('view_name' as never, 'Standard' as never)
                                .is('class_id' as never, null as never)
                                .maybeSingle();
                            if (globalError) {
                                return { error: { status: 'CUSTOM_ERROR', error: globalError.message } };
                            }
                            if (!globalData) return { data: null };
                            const gd = globalData as any;
                            return {
                                data: {
                                    column_order: gd.column_order || [],
                                    column_visibility: gd.column_visibility || {},
                                    column_widths: gd.column_widths || {},
                                    sort_field: gd.sort_field || null,
                                    sort_direction: gd.sort_direction || 'asc',
                                    group_by: gd.group_by || null,
                                    filters: gd.filters || null,
                                }
                            };
                        }
                        return { data: null };
                    }

                    const d = data as any;
                    return {
                        data: {
                            column_order: d.column_order || [],
                            column_visibility: d.column_visibility || {},
                            column_widths: d.column_widths || {},
                            sort_field: d.sort_field || null,
                            sort_direction: d.sort_direction || 'asc',
                            group_by: d.group_by || null,
                            filters: d.filters || null,
                        }
                    };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId }) => [{ type: 'TablePrefs', id: classId || 'global' }],
            keepUnusedDataFor: 300,
        }),

        upsertTablePreferences: builder.mutation<
            void,
            {
                classId: string | null;
                columnOrder?: string[];
                columnVisibility?: Record<string, boolean>;
                columnWidths?: Record<string, number>;
                sortField?: string | null;
                sortDirection?: string;
                groupBy?: string | null;
                filters?: any[] | null;
            }
        >({
            async queryFn({ classId, columnOrder, columnVisibility, columnWidths, sortField, sortDirection, groupBy, filters }) {
                try {
                    const params: Record<string, unknown> = {
                        p_view_name: 'Standard',
                        p_class_id: classId || null,
                    };
                    if (columnOrder !== undefined) params.p_column_order = columnOrder;
                    if (columnVisibility !== undefined) params.p_column_visibility = columnVisibility;
                    if (columnWidths !== undefined) params.p_column_widths = columnWidths;
                    if (sortField !== undefined) params.p_sort_field = sortField;
                    if (sortDirection !== undefined) params.p_sort_direction = sortDirection;
                    if (groupBy !== undefined) params.p_group_by = groupBy;
                    if (filters !== undefined) params.p_filters = filters;

                    const { error } = await supabase.rpc('fn_klassenliste_save_preferences' as never, params as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            // No tag invalidation — local state already reflects the save
        }),

        getCustomColumns: builder.query<
            Array<{ id: string; label: string; type: string; visibility: string; options: string[] | null; class_id: string | null }>,
            { schoolId: string; classId: string }
        >({
            async queryFn({ schoolId, classId }) {
                try {
                    if (!schoolId) return { data: [] };
                    const orFilter = classId === '' ? 'class_id.is.null' : `class_id.eq.${classId},class_id.is.null`;
                    const { data, error } = await supabase
                        .from('custom_columns' as never)
                        .select('id,label,type,visibility,options,class_id,ordering' as never)
                        .eq('school_id' as never, schoolId as never)
                        .or(orFilter as never)
                        .order('ordering' as never, { ascending: true })
                        .order('created_at' as never, { ascending: true });
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: (data || []) as any[] };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId }) => [{ type: 'CustomColumns', id: classId || 'all' }],
            keepUnusedDataFor: 120,
        }),

        upsertCustomColumnValue: builder.mutation<
            void,
            { schoolId: string; studentId: string; columnId: string; value: string }
        >({
            async queryFn({ schoolId, studentId, columnId, value }) {
                try {
                    const { data: userRes } = await supabase.auth.getUser();
                    const uid = userRes?.user?.id || null;
                    const row = {
                        id: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        school_id: schoolId,
                        student_id: studentId,
                        column_id: columnId,
                        value: String(value),
                        updated_by: uid,
                    };
                    const { error } = await supabase
                        .from('custom_column_values' as never)
                        .upsert(row as never, { onConflict: 'school_id,student_id,column_id' } as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
        }),

        updateStudentBuT: builder.mutation<
            void,
            { schoolId: string; studentId: string; butType?: string | null; butValidUntil?: string | null }
        >({
            async queryFn({ studentId, butType, butValidUntil }) {
                try {
                    // SECURITY DEFINER RPC: JWT school context + role check (see fn_klassenliste_update_but).
                    const pButType =
                        butType === undefined || butType === null || String(butType).trim() === ''
                            ? null
                            : String(butType).trim();
                    const pButValid =
                        butValidUntil === undefined || butValidUntil === null || String(butValidUntil).trim() === ''
                            ? null
                            : String(butValidUntil).slice(0, 10);
                    const { error } = await supabase.rpc(
                        'fn_klassenliste_update_but' as never,
                        {
                            p_student_id: studentId,
                            p_but_type: pButType,
                            p_but_valid_until: pButValid,
                        } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            // Liste tab reads BuT from fn_klassenliste_bundle — invalidate so refetch shows saved values
            invalidatesTags: ['ListeOverview'],
        }),

        updateStudentAddress: builder.mutation<
            void,
            { studentId: string; address: string }
        >({
            async queryFn({ studentId, address }) {
                try {
                    const { error } = await supabase.rpc(
                        'fn_klassenliste_update_address' as never,
                        { p_student_id: studentId, p_address: address } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
        }),

        updateStudentExtras: builder.mutation<
            void,
            {
                studentId: string;
                allergies?: string;
                importantInfo?: string;
                sensitiveData?: string;
                deutschHerkunftssprache?: boolean;
                herkunftsspracheCode?: string | null;
            }
        >({
            async queryFn({ studentId, allergies, importantInfo, sensitiveData, deutschHerkunftssprache, herkunftsspracheCode }) {
                try {
                    const params: Record<string, any> = { p_student_id: studentId };
                    if (allergies !== undefined) params.p_allergies = allergies;
                    if (importantInfo !== undefined) params.p_notes = importantInfo;
                    if (sensitiveData !== undefined) params.p_sensitive_data = sensitiveData;
                    if (deutschHerkunftssprache !== undefined) params.p_deutsch_herkunftssprache = deutschHerkunftssprache;
                    if (herkunftsspracheCode !== undefined) params.p_herkunftssprache_code = herkunftsspracheCode;
                    
                    const { error } = await supabase.rpc(
                        'fn_klassenliste_update_extras' as never,
                        params as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
        }),

        updateStudentHauptkontakt: builder.mutation<
            void,
            { studentId: string; contactId: string }
        >({
            async queryFn({ studentId, contactId }) {
                try {
                    const { error } = await supabase.rpc(
                        'fn_set_student_hauptkontakt' as never,
                        { p_student_id: studentId, p_contact_profile_id: contactId } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            // No invalidatesTags here — the optimistic update in useKlassenlisteFields holds
            // for the session; fresh data with is_primary: true is returned on next page load.
        }),

        setStudentPickupIds: builder.mutation<
            void,
            { studentId: string; pickupIds: string[] }
        >({
            async queryFn({ studentId, pickupIds }) {
                try {
                    const { error } = await supabase.rpc(
                        'fn_klassenliste_set_pickup_ids' as never,
                        { p_student_id: studentId, p_pickup_ids: pickupIds } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            // No invalidatesTags — optimistic update handles current session.
            // Bundle is 521 kB; refetching on every add/remove is too expensive.
            // Correct is_authorized flags are returned on next page load.
        }),

        upsertPhotoConsent: builder.mutation<
            string,
            {
                studentId: string;
                printPublications: boolean;
                press: boolean;
                schoolWebsite: boolean;
                projectSites: boolean;
                publishNameAndClass: boolean;
                comment?: string | null;
                validFrom?: string;
                validUntil?: string | null;
                grantedBy?: string | null;
            }
        >({
            async queryFn({ studentId, printPublications, press, schoolWebsite, projectSites, publishNameAndClass, comment, validFrom, validUntil, grantedBy }) {
                try {
                    const { data, error } = await supabase.rpc(
                        'fn_students_upsert_photo_consent' as never,
                        {
                            p_student_id: studentId,
                            p_print_publications: printPublications,
                            p_press: press,
                            p_school_website: schoolWebsite,
                            p_project_sites: projectSites,
                            p_publish_name_and_class: publishNameAndClass,
                            p_comment: comment ?? null,
                            p_valid_from: validFrom ?? new Date().toISOString().split('T')[0],
                            p_valid_until: validUntil ?? null,
                            p_granted_by: grantedBy ?? null,
                        } as never
                    );
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: data as string };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
        }),

        createCustomColumn: builder.mutation<
            { id: string; label: string; type: string; visibility: string; options: string[] | null },
            {
                schoolId: string;
                classId: string | null;
                label: string;
                type: string;
                visibility: string;
                options: string[] | null;
                createdBy: string | null;
            }
        >({
            async queryFn({ schoolId, classId, label, type, visibility, options, createdBy }) {
                try {
                    const newId = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                    const payload = {
                        id: newId,
                        school_id: schoolId,
                        class_id: classId || null,
                        label,
                        type,
                        visibility,
                        options,
                        created_by: createdBy,
                    };
                    const { data, error } = await supabase
                        .from('custom_columns' as never)
                        .insert(payload as never)
                        .select('id,label,type,visibility,options' as never)
                        .maybeSingle();
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    if (!data) {
                        return { error: { status: 'CUSTOM_ERROR', error: 'No data returned from insert' } };
                    }
                    return { data: data as unknown as { id: string; label: string; type: string; visibility: string; options: string[] | null } };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            invalidatesTags: ['CustomColumns', 'ListeOverview'],
        }),

        updateCustomColumn: builder.mutation<
            void,
            { columnId: string; label: string; type: string; visibility: string; options: string[] | null }
        >({
            async queryFn({ columnId, label, type, visibility, options }) {
                try {
                    const { error } = await supabase
                        .from('custom_columns' as never)
                        .update({ label, type, visibility, options } as never)
                        .eq('id' as never, columnId as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            invalidatesTags: ['CustomColumns', 'ListeOverview'],
        }),

        deleteCustomColumn: builder.mutation<
            void,
            { columnId: string }
        >({
            async queryFn({ columnId }) {
                try {
                    const { error } = await supabase
                        .from('custom_columns' as never)
                        .delete()
                        .eq('id' as never, columnId as never);
                    if (error) {
                        return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    }
                    return { data: undefined };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            invalidatesTags: ['CustomColumns', 'ListeOverview'],
        }),

        // ── Lightweight class metadata (prefs + class_info only) ──────────
        getClassMeta: builder.query<
            { preferences: ListeBundleData['preferences']; class_info: string; class_info_print: boolean },
            { classId: string }
        >({
            async queryFn({ classId }) {
                try {
                    const { data, error } = await supabase.rpc(
                        'fn_klassenliste_class_meta' as never,
                        { p_class_id: classId } as never
                    );
                    if (error) return { error: { status: 'CUSTOM_ERROR', error: error.message } };
                    return { data: data as { preferences: ListeBundleData['preferences']; class_info: string; class_info_print: boolean } };
                } catch (e) {
                    return { error: { status: 'CUSTOM_ERROR', error: String(e) } };
                }
            },
            providesTags: (_r, _e, { classId }) => [
                { type: 'TablePrefs', id: classId },
                { type: 'ClassInfo', id: classId },
            ],
            keepUnusedDataFor: 60,
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetListeBundleQuery,
    useGetClassInfoQuery,
    useUpdateClassInfoMutation,
    useGetTablePreferencesQuery,
    useUpsertTablePreferencesMutation,
    useGetCustomColumnsQuery,
    useUpsertCustomColumnValueMutation,
    useUpdateStudentBuTMutation,
    useUpdateStudentAddressMutation,
    useUpdateStudentExtrasMutation,
    useUpdateStudentHauptkontaktMutation,
    useSetStudentPickupIdsMutation,
    useUpsertPhotoConsentMutation,
    useCreateCustomColumnMutation,
    useUpdateCustomColumnMutation,
    useDeleteCustomColumnMutation,
    useGetClassMetaQuery,
} = klassenlisteApi;
